import Container from "../../../components/element/container";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Icon from "../../../components/element/icon";
import FullModal from "../../../components/modal/full.modal";
import { handleCatchError } from "../../../helpers/handle.catch.error";
import { showToast } from "../../../helpers/show.toast";
import { createInsurance } from "../../../redux/reducer/order.reducer";
import DocumentUpload from "../../../components/common/document.upload";
import useWindowDimension from "../../../components/hook/use.window.dimension";

const insurers = [
  "Any",
  "Allianz",
  "Berjaya Sompo",
  "Etiqa Takaful",
  "Generali",
  "Tokio Marine",
  "Zurich Takaful",
];

const insuranceForm = {
  selling_price: "",
  sum_insured_percent: "90%",
  sum_insured_amount: "",
  current_ncd: "None - 0%",
  windscreen_tinted_coverage: "yes",
  windscreen_tinted_amount: "500",
  insurance_company: "Tokio Marine",
  special_perils: "yes",
  takaful_insurance: "yes",
  booklet_version: "english",
  correspondence_address: "",
  ncd_letter: null,
  business_name_card: null,
  parking_address: null,
};

// The form's optional papers, after the NCD letter - the same two the office's
// copy of this form takes, filed under the same names.
const optionalPapers = [
  ["business_name_card", "Business Name Card"],
  ["parking_address", "Parking Address"],
];

const insuranceSchema = Yup.object().shape({
  selling_price: Yup.string().required("Selling price is required"),

  sum_insured_percent: Yup.string()
    .required("Sum insured percentage is required")
    .test(
      "minimum-coverage",
      "Minimum sum insurance coverage is 90%",
      (value) => Number(String(value || "").replace(/%/g, "")) >= 90,
    ),

  sum_insured_amount: Yup.string().required("Sum insured amount is required"),

  current_ncd: Yup.string().required("Current NCD is required"),

  windscreen_tinted_coverage: Yup.string()
    .oneOf(["yes", "no"])
    .required("Please select windscreen coverage"),

  windscreen_tinted_amount: Yup.string().when("windscreen_tinted_coverage", {
    is: "yes",
    then: (schema) => schema.required("Windscreen coverage amount is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  insurance_company: Yup.string().required(
    "Please choose an insurance company",
  ),

  special_perils: Yup.string()
    .oneOf(["yes", "no"])
    .required("Please select special perils option"),

  takaful_insurance: Yup.string()
    .oneOf(["yes", "no"])
    .required("Please select takaful insurance option"),

  booklet_version: Yup.string()
    .oneOf(["english", "chinese"])
    .required("Please select an insurance booklet version"),

  correspondence_address: Yup.string().required(
    "Correspondence address is required",
  ),

  ncd_letter: Yup.mixed().nullable(),
  business_name_card: Yup.mixed().nullable(),
  parking_address: Yup.mixed().nullable(),
});

function RadioChoice({ name, value, checked, onChange, children }) {
  return (
    <label className={`purchase-radio ${checked ? "active" : ""}`}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
      />

      <span className="purchase-radio-control" />

      {children}
    </label>
  );
}

function AmountInput({ prefix, suffix, ...props }) {
  return (
    <span
      className={`purchase-affixed-input ${prefix ? "prefix" : suffix ? "suffix" : ""} ${props.disabled ? "is-disabled" : ""}`}
    >
      {prefix && (
        <span className="input-affix" aria-hidden="true">
          {prefix}
        </span>
      )}
      <input inputMode="decimal" {...props} />
      {suffix && (
        <span className="input-affix" aria-hidden="true">
          {suffix}
        </span>
      )}
    </span>
  );
}

export default function InsuranceRequestModal({
  orderId,
  sellingPrice,
  onClose,
  onComplete,
  readOnly = false,
  initialData = {},
  documents = [],
}) {
  const { t } = useTranslation();
  const { width } = useWindowDimension();
  const dispatch = useDispatch();
  const [amountFocused, setAmountFocused] = useState(false);

  const { createInsuranceLoading } = useSelector((state) => state.common);
  const numericSellingPrice =
    Number(String(sellingPrice || "").replace(/[^0-9.]/g, "")) || 0;
  const initialValues = readOnly
    ? Object.fromEntries(
        Object.keys(insuranceForm).map((key) => [
          key,
          initialData[key] == null ? "" : String(initialData[key]),
        ]),
      )
    : {
        ...insuranceForm,
        selling_price: numericSellingPrice ? String(numericSellingPrice) : "",
        sum_insured_amount: numericSellingPrice
          ? String((numericSellingPrice * 0.9).toFixed(2))
          : "",
      };

  const handleSubmit = (values, { setFieldError, setSubmitting }) => {
    if (readOnly) {
      setSubmitting(false);
      return;
    }
    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        formData.append(key, value);
      }
    });

    dispatch(
      createInsurance({
        order_id: orderId,
        data: formData,
      }),
    )
      .unwrap()
      .then((response) => {
        showToast(
          "Insurance request submitted!",
          "Your insurance request has been submitted successfully.",
          "success",
        );

        onComplete?.(response, values);
      })
      .catch((error) => {
        handleCatchError(error, setFieldError);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <FullModal
      independent
      show={true}
      close={onClose}
      centered
      screenSize="xl"
      className="purchase-form-modal"
      dialogClassName="purchase-form-modal-dialog insurance-modal-dialog"
      contentClassName="purchase-form-modal-content"
      content={
        <section
          className={`purchase-form-dialog identity-dialog insurance-dialog ${readOnly ? "purchase-form-readonly" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="insurance-request-title"
        >
          <header className="purchase-form-header">
            <button
              className="purchase-modal-back"
              type="button"
              onClick={onClose}
              aria-label="Back"
            >
              <Icon icon="ep:back" />
            </button>

            <h1
              className="_label purchase-form-title"
              id="insurance-request-title"
            >
              {t("Insurance Request Form")}
            </h1>
          </header>

          <p className="purchase-form-security security-note-inline">
            <Icon
              tag="span"
              icon="solar:lock-keyhole-linear"
              className="security-lock"
            />
            This information will be used for Sales Order and JPJ ownership
            transfer preparation.
          </p>

          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={readOnly ? undefined : insuranceSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              isSubmitting,
              setFieldTouched,
              setFieldValue,
              setValues,
              touched,
              values,
            }) => (
              <Form className="purchase-form insurance-form">
                {readOnly && (
                  <p className="purchase-review-note">Submitted · View only</p>
                )}
                <fieldset disabled={readOnly}>

                  <Container tag="div" className="purchase-field-grid">
                    <label className="wide">
                      <span>
                        {t("Selling Price (Following VSO)")}{" "}

                      </span>

                      <AmountInput
                        prefix="RM"
                        name="selling_price"
                        value={
                          readOnly && !values.selling_price
                            ? ""
                            : Number(values.selling_price || 0).toLocaleString(
                                "en-MY",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                },
                              )
                        }
                        disabled
                      />

                      {touched.selling_price && errors.selling_price && (
                        <Container tag="div" className="element _errors">
                          {errors.selling_price}
                        </Container>
                      )}
                    </label>

                    <label>
                      <span>{t("Sum Insurance Coverage")}</span>

                      <AmountInput
                        suffix="%"
                        name="sum_insured_percent"
                        value={values.sum_insured_percent.replace(/%/g, "")}
                        placeholder={readOnly ? "Not provided" : "90"}
                        onChange={(event) => {
                          const percent = event.target.value.replace(/%/g, "");
                          if (!/^\d*(\.\d*)?$/.test(percent)) return;
                          const amount =
                            numericSellingPrice &&
                            percent &&
                            Number.isFinite(Number(percent))
                              ? (numericSellingPrice * Number(percent)) / 100
                              : "";
                          setValues({
                            ...values,
                            sum_insured_percent: percent ? `${percent}%` : "",
                            sum_insured_amount:
                              amount === "" ? "" : amount.toFixed(2),
                          });
                        }}
                        onBlur={() =>
                          setFieldTouched("sum_insured_percent", true)
                        }
                      />

                      {touched.sum_insured_percent &&
                        errors.sum_insured_percent && (
                          <Container tag="div" className="element _errors">
                            {errors.sum_insured_percent}
                          </Container>
                        )}
                    </label>

                    <label>
                      {width > 991 && (
                        <span className="text-transparent">-</span>
                      )}

                      <AmountInput
                        prefix="RM"
                        name="sum_insured_amount"
                        aria-label="Sum insured amount"
                        value={
                          amountFocused
                            ? values.sum_insured_amount
                            : values.sum_insured_amount
                              ? Number(
                                  values.sum_insured_amount,
                                ).toLocaleString("en-MY", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                              : ""
                        }
                        placeholder={readOnly ? "Not provided" : "0"}
                        onFocus={() => setAmountFocused(true)}
                        onChange={(event) => {
                          const amount = event.target.value.replace(/,/g, "");
                          if (!/^\d*(\.\d{0,2})?$/.test(amount)) return;
                          const percent =
                            numericSellingPrice &&
                            amount &&
                            Number.isFinite(Number(amount))
                              ? (Number(amount) / numericSellingPrice) * 100
                              : "";
                          setValues({
                            ...values,
                            sum_insured_amount: amount,
                            sum_insured_percent:
                              percent === "" ? "" : `${percent}%`,
                          });
                          setFieldTouched("sum_insured_percent", true, false);
                        }}
                        onBlur={() => {
                          setAmountFocused(false);
                          setFieldTouched("sum_insured_amount", true);
                        }}
                      />

                      {touched.sum_insured_amount &&
                        errors.sum_insured_amount && (
                          <Container tag="div" className="element _errors">
                            {errors.sum_insured_amount}
                          </Container>
                        )}
                    </label>

                    <label>
                      <span>{t("Current NCD")}</span>

                      <select
                        name="current_ncd"
                        value={values.current_ncd}
                        onChange={(event) =>
                          setFieldValue("current_ncd", event.target.value)
                        }
                        onBlur={() => setFieldTouched("current_ncd", true)}
                      >
                        {readOnly && !values.current_ncd && (
                          <option value="">{t("Not provided")}</option>
                        )}
                        <option value="None - 0%">{t("None - 0%")}</option>
                        <option value="25%">25%</option>
                        <option value="30%">30%</option>
                        <option value="38.33%">38.33%</option>
                        <option value="45%">45%</option>
                        <option value="55%">55%</option>
                      </select>

                      {touched.current_ncd && errors.current_ncd && (
                        <Container tag="div" className="element _errors">
                          {errors.current_ncd}
                        </Container>
                      )}
                    </label>

                    {[
                      ["ncd_letter", "NCD Letter"],
                      ...optionalPapers,
                    ].map(([key, label]) => (
                      <DocumentUpload
                        key={key}
                        name={key}
                        label={t(label)}
                        optional
                        note={key !== "ncd_letter" ? t("Needed for a purchase above RM1 million") : undefined}
                        file={values[key]}
                        readOnly={readOnly}
                        document={documents.find((document) => document.group === key)}
                        error={touched[key] && errors[key]}
                        onChange={(file) => {
                          setFieldTouched(key, true, false);
                          setFieldValue(key, file);
                        }}
                      />
                    ))}

                    <Container tag="div" className="wide insurance-choice-row">
                      <span className="insurance-choice-label">
                        {t("Request Windscreen Coverage With Tinted")}
                      </span>

                      <Container tag="div" className="insurance-choice-options">
                        {["yes", "no"].map((value) => (
                          <RadioChoice
                            name="windscreen_tinted_coverage"
                            value={value}
                            checked={
                              values.windscreen_tinted_coverage === value
                            }
                            onChange={() => {
                              setFieldValue(
                                "windscreen_tinted_coverage",
                                value,
                              );
                              setFieldTouched(
                                "windscreen_tinted_coverage",
                                true,
                                false,
                              );

                              if (value === "no") {
                                setFieldValue("windscreen_tinted_amount", "");
                              }
                            }}
                            key={value}
                          >
                            {value === "yes" ? "Yes" : "No"}
                          </RadioChoice>
                        ))}
                      </Container>
                    </Container>

                    {touched.windscreen_tinted_coverage &&
                      errors.windscreen_tinted_coverage && (
                        <Container tag="div" className="wide element _errors">
                          {errors.windscreen_tinted_coverage}
                        </Container>
                      )}

                    {values.windscreen_tinted_coverage === "yes" && (
                      <label className="wide">
                        <AmountInput
                          prefix="RM"
                          name="windscreen_tinted_amount"
                          aria-label="Windscreen coverage amount"
                          value={values.windscreen_tinted_amount}
                          placeholder="0"
                          disabled={values.windscreen_tinted_coverage === "no"}
                          onChange={(event) =>
                            setFieldValue(
                              "windscreen_tinted_amount",
                              event.target.value,
                            )
                          }
                          onBlur={() =>
                            setFieldTouched("windscreen_tinted_amount", true)
                          }
                        />

                        {touched.windscreen_tinted_amount &&
                          errors.windscreen_tinted_amount && (
                            <Container tag="div" className="element _errors">
                              {errors.windscreen_tinted_amount}
                            </Container>
                          )}
                      </label>
                    )}

                    <label className="wide">
                      <span>
                        {t("Correspondence Address")}
                        {!readOnly && (
                          <span style={{ color: "#ff5353" }}> *</span>
                        )}
                      </span>

                      <input
                        readOnly={readOnly}
                        name="correspondence_address"
                        value={values.correspondence_address}
                        placeholder={
                          readOnly
                            ? "Not provided"
                            : "e.g 13, Jalan 3/31, 41000 Klang, Selangor"
                        }
                        onChange={(event) =>
                          setFieldValue(
                            "correspondence_address",
                            event.target.value,
                          )
                        }
                        onBlur={() =>
                          setFieldTouched("correspondence_address", true)
                        }
                      />

                      {touched.correspondence_address &&
                        errors.correspondence_address && (
                          <Container tag="div" className="element _errors">
                            {errors.correspondence_address}
                          </Container>
                        )}
                    </label>

                    {[
                      ["special_perils", "With Special Perils"],
                      [
                        "takaful_insurance",
                        "Takaful Insurance (for takaful loan)",
                      ],
                    ].map(([key, label]) => (
                      <Container tag="div" className="wide insurance-choice-row" key={key}>
                        <span className="insurance-choice-label">{label}</span>

                        <Container tag="div" className="insurance-choice-options">
                          {["yes", "no"].map((value) => (
                            <RadioChoice
                              name={key}
                              value={value}
                              checked={values[key] === value}
                              onChange={() => {
                                setFieldValue(key, value);
                                setFieldTouched(key, true, false);
                              }}
                              key={value}
                            >
                              {value === "yes" ? "Yes" : "No"}
                            </RadioChoice>
                          ))}
                        </Container>

                        {touched[key] && errors[key] && (
                          <Container tag="div" className="element _errors">{errors[key]}</Container>
                        )}
                      </Container>
                    ))}

                    <Container tag="div" className="wide insurance-choice-row booklet-row">
                      <span className="insurance-choice-label">
                        {t("Prefer Booklet Language")}
                      </span>

                      <Container tag="div" className="insurance-choice-options">
                        {[
                          ["english", "English Version"],
                          ["chinese", "Chinese Version"],
                        ].map(([value, label]) => (
                          <RadioChoice
                            name="booklet_version"
                            value={value}
                            checked={values.booklet_version === value}
                            onChange={() => {
                              setFieldValue("booklet_version", value);
                              setFieldTouched("booklet_version", true, false);
                            }}
                            key={value}
                          >
                            {label}
                          </RadioChoice>
                        ))}
                      </Container>

                      {touched.booklet_version && errors.booklet_version && (
                        <Container tag="div" className="element _errors">
                          {errors.booklet_version}
                        </Container>
                      )}
                    </Container>

                    <Container tag="div" className="wide insurance-company-list">
                      <span className="insurance-company-title">
                        {t("Preference Insurance Company :")}
                      </span>

                      <Container tag="div" className="insurance-company-options">
                        {insurers.map((insurer) => (
                          <RadioChoice
                            name="insurance_company"
                            value={insurer}
                            checked={values.insurance_company === insurer}
                            onChange={() => {
                              setFieldValue("insurance_company", insurer);
                              setFieldTouched("insurance_company", true, false);
                            }}
                            key={insurer}
                          >
                            {insurer}
                          </RadioChoice>
                        ))}
                      </Container>

                      {touched.insurance_company &&
                        errors.insurance_company && (
                          <Container tag="div" className="element _errors">
                            {errors.insurance_company}
                          </Container>
                        )}
                    </Container>



                  </Container>
                </fieldset>

                {!readOnly && (
                  <button
                    type="submit"
                    className="purchase-form-submit"
                    disabled={createInsuranceLoading || isSubmitting}
                  >
                    {createInsuranceLoading || isSubmitting
                      ? t("Submitting...")
                      : t("Confirm")}
                  </button>
                )}
              </Form>
            )}
          </Formik>
        </section>
      }
    />
  );
}
